import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { icon } from "../../../components/assets/assets";
import NormalInputField from "../../../components/input-fields/normal-input-field";
import PasswordInputField from "../../../components/input-fields/password-input-field";
import NormalButton from "../../../components/buttons/normal-button";
import WarningToast from "../../../components/toasts/warning-toast";
import SuccessToast from "../../../components/toasts/success-toast";
import ErrorToast from "../../../components/toasts/error-toast";
import MobileNumberInputField from "../../../components/input-fields/mobile-number-input-field";
import {
  checkPasswordStrength,
  validateEmailInput,
} from "../../../utils/validationRegex";
import {
  getCountryCode,
  getPhoneNumber,
} from "../../../utils/phoneNumberUtils";
import { saveCandidate } from "../../../redux/actions/sign-up-actions";
import AmpleHireLoader from "../../../components/loaders/ample-hire-loader";

const SignupForm = () => {
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    whatsappNumber: "",
    mobileNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [allowSubmit, setAllowSubmit] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [mobileVerified, setMobileVerified] = useState(false);
  const [whastappVerified, setWhastappVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiCalled, setApiCalled] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleOnChangeNewUser = (e) => {
    const { name, value } = e.target;
    const nameRegex = /^[A-Za-z\s]*$/;
    if (
      (name === "firstName" || name === "lastName") &&
      !nameRegex.test(value)
    ) {
      return;
    }
    setNewUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleOnClickVerifyContactNo = () => {
    const { firstName, lastName, mobileNumber, whatsappNumber } = newUser;
    if (
      !apiCalled &&
      firstName &&
      lastName &&
      (mobileNumber || whatsappNumber)
    ) {
      handleSaveCandidate(newUser);
    }
  };

  const handleConfirmPassword = () =>
    newUser.password === newUser.confirmPassword;

  const handleSaveCandidate = async (updatedUser) => {
    setIsLoading(true);
    const res = await dispatch(
      saveCandidate({
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName,
        mobileNumber1: getPhoneNumber(updatedUser.mobileNumber),
        whatsappNumber: getPhoneNumber(updatedUser.whatsappNumber),
        primaryEmailId: updatedUser.email,
        mobileNumber1CountryCode: getCountryCode(updatedUser.mobileNumber)
          ? "+" + getCountryCode(updatedUser.mobileNumber)
          : "",
        whatsappNumberCountryCode: getCountryCode(updatedUser.whatsappNumber)
          ? "+" + getCountryCode(updatedUser.whatsappNumber)
          : "",
        whatsappNumberVerified: whastappVerified,
        mobileNumber1Verified: mobileVerified,
      }),
    );
    setIsLoading(false);
    setApiCalled(true);
    if (res.payload?.success || res.success) {
      SuccessToast(
        res.payload?.message || res.message || "User Created Successfully!",
      );
    }
  };

  useEffect(() => {
    const { firstName, lastName, email, mobileNumber, whatsappNumber } =
      newUser;
    if (!firstName || !lastName) {
      setFeedbackMessage("Please enter your First Name and Last Name.");
    } else if (!mobileNumber && !whatsappNumber) {
      setFeedbackMessage(
        "Please enter either your Mobile Number or WhatsApp Number.",
      );
    } else if (!email) {
      setFeedbackMessage("Please enter your Email Id.");
    } else {
      setFeedbackMessage("");
    }
    setAllowSubmit(
      firstName &&
        lastName &&
        email &&
        (mobileNumber.length >= 4 || whatsappNumber.length >= 4),
    );
  }, [newUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newUser.firstName || !newUser.lastName)
      return WarningToast("Please enter your name.");
    if (!newUser.email) return WarningToast("Please enter email.");
    if (!newUser.whatsappNumber && !newUser.mobileNumber)
      return WarningToast("Please enter Mobile number or Whatsapp number!");
    if (
      (newUser.password && !newUser.confirmPassword) ||
      (!newUser.password && newUser.confirmPassword)
    )
      return WarningToast("Please enter both passwords");
    const passwordStrength = checkPasswordStrength(newUser.password);
    if (newUser.password && passwordStrength)
      return WarningToast(passwordStrength);
    if (newUser.password && !handleConfirmPassword())
      return WarningToast("Confirm password does not match.");

    setIsLoading(true);
    const res = await dispatch(
      saveCandidate({
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        password: newUser.password,
        mobileNumber1: getPhoneNumber(newUser.mobileNumber),
        whatsappNumber: getPhoneNumber(newUser.whatsappNumber),
        primaryEmailId: newUser.email,
        mobileNumber1CountryCode: getCountryCode(newUser.mobileNumber)
          ? "+" + getCountryCode(newUser.mobileNumber)
          : "",
        mobileNumber2CountryCode: "",
        whatsappNumberCountryCode: getCountryCode(newUser.whatsappNumber)
          ? "+" + getCountryCode(newUser.whatsappNumber)
          : "",
        whatsappNumberVerified: whastappVerified,
        mobileNumber1Verified: mobileVerified,
        mobileNumber2Verified: false,
      }),
    );
    setIsLoading(false);
    if (res.payload?.success || res.success) {
      SuccessToast(
        res.payload?.message || res.message || "User Created Successfully!",
      );
      navigate("/signin");
    } else {
      ErrorToast(res.message || "Error creating User!");
    }
  };

  return (
    <div className="signup-card">
      {isLoading && <AmpleHireLoader />}

      <div className="signup-eyebrow">Get Started</div>
      <h3 className="signup-card-title">Create Your Account</h3>
      <p className="signup-card-subtitle">
        Fill in your details to get started
      </p>

      <form onSubmit={handleSubmit} className="signup-form" autoComplete="off">
        {/* Name Row */}
        <div className="signup-row">
          <NormalInputField
            divTagCssClasses="input-field signup-field"
            type="text"
            inputTagIdAndName="firstName"
            placeholder="First Name"
            value={newUser.firstName}
            onChange={handleOnChangeNewUser}
            labelText="First Name"
            required={true}
          />
          <NormalInputField
            divTagCssClasses="input-field signup-field"
            type="text"
            inputTagIdAndName="lastName"
            placeholder="Last Name"
            value={newUser.lastName}
            onChange={handleOnChangeNewUser}
            labelText="Last Name"
            required={true}
          />
        </div>

        {/* Phone Row */}
        <div className="signup-row signup-phone-row">
          <MobileNumberInputField
            divTagCssClasses="input-field signup-field"
            type="number"
            inputTagIdAndName="whatsappNumber"
            placeholder="WhatsApp No"
            value={newUser.whatsappNumber}
            onChange={(e) =>
              handleOnChangeNewUser({
                target: { name: "whatsappNumber", value: e },
              })
            }
            onClickVerify={() => {}}
            required={false}
            leftIconSrc={icon.whatsappIcon}
            labelText="WhatsApp No"
            isVerified={whastappVerified}
            setIsVerified={setWhastappVerified}
            showVerification={true}
            prevNav="/signup"
          />
          <MobileNumberInputField
            divTagCssClasses="input-field signup-field"
            type="number"
            inputTagIdAndName="mobileNumber"
            placeholder="Mobile No"
            value={newUser.mobileNumber}
            onChange={(e) =>
              handleOnChangeNewUser({
                target: { name: "mobileNumber", value: e },
              })
            }
            onClickVerify={() => {}}
            required={false}
            leftIconSrc={icon.mobilePhoneIcon}
            labelText="Mobile No"
            isVerified={mobileVerified}
            setIsVerified={setMobileVerified}
            showVerification={true}
            prevNav="/signup"
          />
        </div>
        <p className="sp-phone-hint">Enter at least one number</p>

        {/* Email */}
        <NormalInputField
          divTagCssClasses="input-field signup-field"
          inputTagCssClasses="validate"
          type="email"
          inputTagIdAndName="email"
          placeholder="you@company.com"
          value={newUser.email}
          onBlur={(e) => validateEmailInput(e, WarningToast)}
          onChange={handleOnChangeNewUser}
          leftIconSrc={icon.emailAddressIcon}
          labelText="Email Address"
          required={true}
        />

        {/* Password Row */}
        <div className="signup-row">
          <PasswordInputField
            divTagCssClasses="signup-field"
            inputTagIdAndName="password"
            value={newUser.password}
            onChange={handleOnChangeNewUser}
            placeholder="Min 8 characters"
            leftIconSrc={icon.lockIcon}
            labelText="Password"
          />
          <PasswordInputField
            divTagCssClasses="signup-field"
            inputTagIdAndName="confirmPassword"
            placeholder="Re-enter password"
            value={newUser.confirmPassword}
            onChange={handleOnChangeNewUser}
            leftIconSrc={icon.lockIcon}
            labelText="Confirm Password"
          />
        </div>

        {/* Feedback */}
        {feedbackMessage && (
          <div className="signup-feedback">
            <span>{feedbackMessage}</span>
          </div>
        )}

        {/* Submit */}
        <NormalButton
          buttonTagCssClasses={`btn-large signup-submit-btn ${!allowSubmit ? "disabled-btn" : ""}`}
          buttonText="Create Account"
          disabled={!allowSubmit}
          onClick={handleSubmit}
        />

        {/* Terms */}
        <p className="signup-terms">
          By signing up, you agree to our <a href="#">Terms of Service</a> and{" "}
          <a href="#">Privacy Policy</a>.
        </p>

        {/* Footer */}
        <div className="signup-footer-link">
          Already have an account? <Link to="/signin">Sign in</Link>
        </div>
      </form>
    </div>
  );
};

export default SignupForm;
